import type { EliteInsights, EliteInsightsCta, TaxSummaryBlock } from '../../../types/misc.types'
import { buildRebalanceInsightDisplay } from '../rebalanceInsightDisplay'
import {
  buildTaxSummaryDisplay,
  NON_ELITE_TAX_SUMMARY_SUBTITLE,
  resolveTaxSummaryBlock
} from '../taxSummaryDisplay'
import { lightImpactVibrate, postCTA } from '../../../utils/appBridge'
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import { NewEliteLandingEventId, trackNewEliteUserClicked } from '../analytics/newEliteLandingAnalytics'
import { ChevronRight } from '../../tax-harvesting/components/plan/EligibleFund'
import { useEliteBuyCtaExtras } from '../../../components/elite/EliteBuyCtaExtrasContext'

const ASSETS = {
  crownLeft: '/images/new-elite/insights/crown-left.svg',
  crownRight: '/images/new-elite/insights/crown-right.svg',
  rebalance: '/images/new-elite/insights/rebalance.svg',
  regularToDirect: '/images/new-elite/insights/regular-to-direct.svg',
  tax: '/images/new-elite/insights/tax.svg',
  powerSelect: '/images/new-elite/insights/power-select.svg',
  eliteLens: '/images/new-elite/insights/elite-lens.svg'
} as const

export type NewEliteEliteInsightsTilesProps = {
  eliteInsights?: EliteInsights
  numberOfFundsToRebalance?: number
}

export default function NewEliteEliteInsightsTiles({
  eliteInsights,
  numberOfFundsToRebalance,
}: NewEliteEliteInsightsTilesProps) {
  const rebalanceCta = eliteInsights?.rebalanceCtaRequest
  const taxSummary = resolveTaxSummaryBlock(eliteInsights?.taxSummary, eliteInsights?.taxSummaryCta)
  const powerSelectCta = eliteInsights?.powerSelectCtaRequest
  const eliteLensCta = eliteInsights?.eliteLensCtaRequest
  const regularToDirectCta = eliteInsights?.regularToDirect?.regularToDirectCtaRequest
  const numberOfFundsLoweringReturns = eliteInsights?.regularToDirect?.fundsCount
  const showTax = hasEliteInsightCta(taxSummary?.cta)
  const showRebalance = hasEliteInsightCta(rebalanceCta)
  const showRegularToDirect = hasEliteInsightCta(regularToDirectCta) && numberOfFundsLoweringReturns && numberOfFundsLoweringReturns > 0
  const showPowerSelect = hasEliteInsightCta(powerSelectCta)
  const showEliteLens = hasEliteInsightCta(eliteLensCta)
  const rebalanceHighlight = showRebalance
    ? buildRebalanceInsightDisplay(eliteInsights, numberOfFundsToRebalance ?? 0)
    : undefined
  const visibleTileKeys = [
    showTax ? 'tax' : null,
    showRebalance ? 'rebalance' : null,
    showRegularToDirect ? 'regularToDirect' : null,
    showPowerSelect ? 'powerSelect' : null,
    showEliteLens ? 'eliteLens' : null
  ].filter((key): key is InsightTileKey => key != null)

  return (
    <div className='flex flex-col gap-7 bg-white'>
      <div className='flex flex-col items-start px-3 gap-1'>
        <p className='text-[13px] leading-[1.4] text-[#6A6E6E]'>
          {eliteInsights?.userElite ? 'You have exclusive access to' : 'Get exclusive access to'}
        </p>
        <div className='flex items-center gap-1.5'>
          <svg width="76" height="20" viewBox="0 0 76 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path fill-rule="evenodd" clip-rule="evenodd" d="M8.99369 1.85287C9.32592 1.32649 9.16537 0.632419 8.63509 0.302616C8.1048 -0.0271695 7.40559 0.132199 7.07334 0.658585L6.58865 1.42654C6.25641 1.95293 6.41695 2.647 6.94726 2.97678C7.47754 3.30659 8.17675 3.14722 8.50898 2.62083L8.99369 1.85287ZM2.95804 3.17844C3.41236 2.74601 4.12988 2.75659 4.56066 3.20207L5.64358 4.32193L7.46416 3.82138C8.06929 3.65501 8.69009 4.00504 8.85076 4.6032C9.01146 5.20133 8.65118 5.8211 8.04606 5.98746L5.575 6.66683C5.17403 6.77709 4.74691 6.66199 4.46144 6.36679L2.91543 4.76805C2.48465 4.32258 2.50373 3.61089 2.95804 3.17844ZM2.79129 7.01C2.31782 6.60948 1.60091 6.6685 1.19003 7.14184C0.779153 7.61516 0.829896 8.32355 1.30337 8.72408L3.48801 10.5721C3.82135 10.8541 4.29215 10.9173 4.6951 10.7343L7.33594 9.53452C7.90827 9.2745 8.1649 8.60741 7.90912 8.04451C7.65334 7.48163 6.98201 7.2361 6.40967 7.49612L4.42944 8.39575L2.79129 7.01ZM7.33755 13.3876C7.87899 13.0743 8.06321 12.3857 7.74902 11.8496C7.43483 11.3136 6.74121 11.133 6.19975 11.4464L4.44032 12.4647L2.26425 11.5931C1.68444 11.3608 1.02309 11.6393 0.787071 12.2152C0.551053 12.7911 0.829747 13.4462 1.40955 13.6784L4.10078 14.7565C4.42572 14.8866 4.79359 14.86 5.09703 14.6844L7.33755 13.3876Z" fill="#CC5C2D" />
            <path d="M10.1697 14.5021L9.38451 15.9939C10.198 16.0582 12.3211 16.5389 14.3057 17.9473C14.7954 18.3339 14.8766 19.0415 14.4872 19.5277C14.0976 20.0138 13.3848 20.0944 12.8951 19.7078C12.5674 19.3554 11.3186 18.5351 8.94514 18.0728C8.55343 18.0217 8.19104 18.0591 7.84175 18.0728L7.70078 18.0591C7.68479 18.0604 7.66873 18.0613 7.65258 18.0618L3.92396 18.2011C3.2922 18.2247 2.74839 17.7465 2.70934 17.1331C2.67028 16.5194 3.15073 16.003 3.78248 15.9794L6.85475 15.8647L8.1232 13.4548C8.40826 12.9132 9.09746 12.7086 9.66259 12.9978C10.2277 13.287 10.4548 13.9605 10.1697 14.5021Z" fill="#CC5C2D" />
            <path d="M17.139 17V2.5H28.014V4.91667H20.139V8.125H27.5973V10.5417H20.139V14.5417H28.0973V17H17.139ZM30.8656 17V1.91667H33.5322V17H30.8656ZM36.8174 17V6.66667H39.484V17H36.8174ZM37.9215 5C37.491 5 37.1299 4.86111 36.8382 4.58333C36.5604 4.29167 36.4215 3.93056 36.4215 3.5V3.41667C36.4215 2.98611 36.5604 2.63194 36.8382 2.35417C37.1299 2.0625 37.491 1.91667 37.9215 1.91667H38.3799C38.8104 1.91667 39.1646 2.0625 39.4424 2.35417C39.734 2.63194 39.8799 2.98611 39.8799 3.41667V3.5C39.8799 3.91667 39.734 4.27083 39.4424 4.5625C39.1646 4.85417 38.8104 5 38.3799 5H37.9215ZM43.3213 12.9792V8.83333H41.5088V6.66667H43.4046V3.52083H45.9255V6.66667H48.9255V8.83333H45.988V12.9792H43.3213ZM47.6338 17.25C46.1755 17.25 45.0921 16.8681 44.3838 16.1042C43.6755 15.3403 43.3213 14.3611 43.3213 13.1667V12.9792H45.988V13.2292C45.988 13.7986 46.1616 14.2361 46.5088 14.5417C46.856 14.8333 47.3491 14.9792 47.988 14.9792C48.4602 14.9792 48.9741 14.9097 49.5296 14.7708V17C48.9185 17.1667 48.2866 17.25 47.6338 17.25ZM50.3656 11.8125C50.3656 10.7014 50.5947 9.74306 51.0531 8.9375C51.5253 8.11806 52.1919 7.49306 53.0531 7.0625C53.9142 6.63194 54.9142 6.41667 56.0531 6.41667C57.2614 6.41667 58.2753 6.65278 59.0947 7.125C59.9142 7.59722 60.5253 8.24306 60.9281 9.0625C61.3308 9.86806 61.5322 10.7708 61.5322 11.7708C61.5322 12.0069 61.5183 12.2917 61.4906 12.625H51.7406L53.1572 11.4792C52.9767 12.6875 53.1433 13.6042 53.6572 14.2292C54.185 14.8403 54.9767 15.1458 56.0322 15.1458C56.7128 15.1458 57.2753 15.0139 57.7197 14.75C58.1781 14.4861 58.4975 14.1528 58.6781 13.75H61.4281C61.1781 14.8333 60.6017 15.6875 59.6989 16.3125C58.7961 16.9375 57.5947 17.25 56.0947 17.25C54.3031 17.25 52.9003 16.7847 51.8864 15.8542C50.8725 14.9097 50.3656 13.5625 50.3656 11.8125ZM53.0947 11.8542L51.7406 10.875H58.8864C58.8864 10.1667 58.6433 9.58333 58.1572 9.125C57.6711 8.65278 56.9697 8.41667 56.0531 8.41667C54.9975 8.41667 54.2128 8.73611 53.6989 9.375C53.185 10.0139 52.9836 10.8403 53.0947 11.8542Z" fill="#CC5C2D" />
            <g clip-path="url(#clip0_81524_78429)">
              <path fill-rule="evenodd" clip-rule="evenodd" d="M67.0224 1.85287C66.6948 1.32649 66.8531 0.632419 67.3761 0.302616C67.899 -0.0271695 68.5885 0.132199 68.9162 0.658585L69.3942 1.42654C69.7218 1.95293 69.5635 2.647 69.0405 2.97678C68.5176 3.30659 67.8281 3.14722 67.5004 2.62083L67.0224 1.85287ZM72.9745 3.17844C72.5265 2.74601 71.8189 2.75659 71.3941 3.20207L70.3262 4.32193L68.5308 3.82138C67.934 3.65501 67.3218 4.00504 67.1634 4.6032C67.0049 5.20133 67.3602 5.8211 67.9569 5.98746L70.3938 6.66683C70.7892 6.77709 71.2104 6.66199 71.4919 6.36679L73.0166 4.76805C73.4414 4.32258 73.4226 3.61089 72.9745 3.17844ZM73.139 7.01C73.6059 6.60948 74.3129 6.6685 74.7181 7.14184C75.1233 7.61516 75.0732 8.32355 74.6063 8.72408L72.4519 10.5721C72.1232 10.8541 71.6589 10.9173 71.2615 10.7343L68.6572 9.53452C68.0928 9.2745 67.8397 8.60741 68.092 8.04451C68.3442 7.48163 69.0063 7.2361 69.5707 7.49612L71.5235 8.39575L73.139 7.01ZM68.6556 13.3876C68.1217 13.0743 67.94 12.3857 68.2499 11.8496C68.5597 11.3136 69.2437 11.133 69.7777 11.4464L71.5128 12.4647L73.6587 11.5931C74.2305 11.3608 74.8827 11.6393 75.1155 12.2152C75.3482 12.7911 75.0734 13.4462 74.5016 13.6784L71.8476 14.7565C71.5272 14.8866 71.1644 14.86 70.8651 14.6844L68.6556 13.3876Z" fill="#CC5C2D" />
              <path d="M65.8627 14.5021L66.637 15.9939C65.8348 16.0582 63.7411 16.5389 61.7839 17.9473C61.301 18.3339 61.2209 19.0415 61.6049 19.5277C61.9891 20.0138 62.6921 20.0944 63.175 19.7078C63.4982 19.3554 64.7297 18.5351 67.0703 18.0728C67.4566 18.0217 67.814 18.0591 68.1584 18.0728L68.2974 18.0591C68.3132 18.0604 68.329 18.0613 68.345 18.0618L72.022 18.2011C72.645 18.2247 73.1813 17.7465 73.2198 17.1331C73.2583 16.5194 72.7845 16.003 72.1615 15.9794L69.1318 15.8647L67.8809 13.4548C67.5998 12.9132 66.9201 12.7086 66.3628 12.9978C65.8055 13.287 65.5816 13.9605 65.8627 14.5021Z" fill="#CC5C2D" />
            </g>
            <defs>
              <clipPath id="clip0_81524_78429">
                <rect width="16.6667" height="20" fill="white" transform="matrix(-1 0 0 1 75.8887 0)" />
              </clipPath>
            </defs>
          </svg>
          <p className='text-[20px] font-medium leading-[1.5] text-[#232323]'>Insights</p>
        </div>
      </div>

      {visibleTileKeys.length > 0 ? (
        <div className='flex flex-col gap-4'>
          <div className='overflow-hidden rounded-xl border border-[#EBF2F2]'>
            {visibleTileKeys.map((key, index) => {
              const hasTopRadius = index === 0
              const hasBottomRadius = index === visibleTileKeys.length - 1
              if (key === 'tax' && taxSummary) {
                return (
                  <TaxInsightTapBlock
                    key={key}
                    iconUrl={ASSETS.tax}
                    taxSummary={taxSummary}
                    userElite={eliteInsights?.userElite ?? false}
                    hasTopRadius={hasTopRadius}
                    hasBottomRadius={hasBottomRadius}
                  />
                )
              }
              if (key === 'rebalance') {
                return (
                  <InsightTapRow
                    key={key}
                    iconUrl={ASSETS.rebalance}
                    title='Power Rebalance'
                    subtitle='Know exactly which funds to switch'
                    highlight={rebalanceHighlight?.text}
                    highlightTone={rebalanceHighlight?.tone}
                    cta={rebalanceCta}
                    hasTopRadius={hasTopRadius}
                    hasBottomRadius={hasBottomRadius}
                  />
                )
              }
              if (key === 'regularToDirect') {
                return (
                  <InsightTapRow
                    key={key}
                    iconUrl={ASSETS.regularToDirect}
                    title='Regular to Direct Plan'
                    subtitle='Avoid commissions tax-efficiently'
                    highlight={
                      numberOfFundsLoweringReturns && numberOfFundsLoweringReturns > 0
                        ? `${numberOfFundsLoweringReturns} fund${numberOfFundsLoweringReturns === 1 ? '' : 's'} lowering your returns`
                        : undefined
                    }
                    cta={regularToDirectCta}
                    hasTopRadius={hasTopRadius}
                    hasBottomRadius={hasBottomRadius}
                  />
                )
              }
              if (key === 'powerSelect') {
                return (
                  <InsightTapRow
                    key={key}
                    iconUrl={ASSETS.powerSelect}
                    title='Power Select'
                    subtitle='Compare funds by rank, returns & risk'
                    cta={powerSelectCta}
                    hasTopRadius={hasTopRadius}
                    hasBottomRadius={hasBottomRadius}
                  />
                )
              }
              return (
                <InsightTapRow
                  key={key}
                  iconUrl={ASSETS.eliteLens}
                  title='Elite Lens'
                  subtitle='Your exclusive weekly newsletter'
                  cta={eliteLensCta}
                  hasTopRadius={hasTopRadius}
                  hasBottomRadius={hasBottomRadius}
                />
              )
            })}
          </div>
        </div>
      ) : null}
    </div>
  )
}

function handleInsightCta(cta: EliteInsightsCta | undefined) {
  if (!cta?.url) {
    return
  }
  lightImpactVibrate()
  postCTA(JSON.stringify(cta))
}

export function InsightTapRow({
  iconUrl,
  title,
  subtitle,
  highlight,
  highlightTone = 'orange',
  cta,
  hasTopRadius = true,
  hasBottomRadius = false
}: {
  iconUrl: string
  title: string
  subtitle: string
  highlight?: string
  highlightTone?: 'orange' | 'success'
  cta?: EliteInsightsCta
  hasTopRadius?: boolean
  hasBottomRadius?: boolean
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const canTap = Boolean(cta?.url)
  return (
    <div
      onClick={() => {
        if (!canTap) return
        trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.InsightTile, {
          ...eliteBuyCtaExtras,
          buttonLabel: title
        })
        handleInsightCta(cta)
      }}
      className={`flex items-center justify-between border-b border-[#F0F3F5] bg-white px-3 py-[22px] ${hasTopRadius ? 'rounded-t-xl' : ''} ${hasBottomRadius ? 'rounded-b-xl border-b-0' : ''} ${canTap ? 'cursor-pointer' : ''}`}
    >
      <div className='flex items-start gap-4'>
        <div className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white gap-[2px]'>
          <img alt='' className='size-[18px] object-contain' src={iconUrl} />
        </div>
        <div className='flex flex-col gap-[2px]'>
          <p className='text-[14px] font-medium leading-[1.3] text-[#232323]'>{title}</p>
          <p className='text-[13px] leading-[1.5] text-[#8E9393]'>{subtitle}</p>
          {highlight ? (
            <p
              className={`text-[13px] font-medium leading-[1.5] whitespace-pre-line max-w-[290px] ${highlightTone === 'success' ? 'text-status-text-in-form' : 'text-orange30'}`}
            >
              {highlight}
            </p>
          ) : null}
        </div>
      </div>
      <p className='shrink-0 text-xl leading-none text-[#3D3D3D]'>
        <ChevronRight />
      </p>
    </div>
  )
}

export function TaxInsightTapBlock({
  iconUrl,
  taxSummary,
  userElite = false,
  hasTopRadius = true,
  hasBottomRadius = false
}: {
  iconUrl: string
  taxSummary: TaxSummaryBlock
  userElite?: boolean
  hasTopRadius?: boolean
  hasBottomRadius?: boolean
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const { descriptionLine, summaryHighlight } = buildTaxSummaryDisplay(taxSummary)
  const subtitle = userElite ? descriptionLine : NON_ELITE_TAX_SUMMARY_SUBTITLE
  const highlight = userElite ? summaryHighlight : undefined
  const canTap = Boolean(taxSummary.cta?.url)
  return (
    <div
      onClick={() => {
        if (!canTap) return
        trackNewEliteUserClicked(analyticsScreen, NewEliteLandingEventId.InsightTile, {
          ...eliteBuyCtaExtras,
          buttonLabel: 'Tax Summary'
        })
        handleInsightCta(taxSummary.cta)
      }}
      className={`flex items-center justify-between border-b border-[#F0F3F5] bg-white px-3 py-[22px] ${hasTopRadius ? 'rounded-t-xl' : ''} ${hasBottomRadius ? 'rounded-b-xl border-b-0' : ''} ${canTap ? 'cursor-pointer' : ''}`}
    >
      <div className='flex items-start gap-4'>
        <div className='flex size-10 shrink-0 items-center justify-center rounded-xl bg-white gap-[2px]'>
          <img alt='' className='size-[18px] object-contain' src={iconUrl} />
        </div>
        <div className='flex flex-col gap-[2px]'>
          <p className='text-[14px] font-medium leading-[1.3] text-[#232323]'>Tax Summary</p>
          <p
            className={`text-[13px] leading-[1.5] ${userElite ? 'text-grey50' : 'font-medium text-orange30'}`}
          >
            {subtitle}
          </p>
          {highlight ? (
            <p className='text-[13px] font-medium leading-[1.5] text-orange10'>{highlight}</p>
          ) : null}
        </div>
      </div>
      <p className='shrink-0 text-xl leading-none text-[#3D3D3D]'>
        <ChevronRight />
      </p>
    </div>
  )
}

type InsightTileKey = 'tax' | 'rebalance' | 'regularToDirect' | 'powerSelect' | 'eliteLens'

export function hasEliteInsightCta(cta?: EliteInsightsCta): boolean {
  return Boolean(cta?.url)
}
